import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Template, TemplateField, TemplateChildResource } from 'types';

import FieldDefault from 'components/field-default';

import IconTask from 'icons/icon-task';
import IconSubtask from 'icons/icon-subtask';
import IconRoadmap from 'icons/icon-roadmap';
import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowRight from 'icons/icon-arrow-right';

const START_FIELDS = ['start_date', 'start'];
const END_FIELDS = ['due_date', 'end'];

interface FieldItemProps extends TemplateField {
    resourceType: TemplateChildResource['type'],
    onChange: (offset: TemplateField['offset'], type: TemplateField['type']) => void
}

const FieldItem = ({ type, resourceType, offset, onChange }: FieldItemProps) => {
    const { t } = useTranslation('common');

    const handleChange = useCallback((value: string) => {
        onChange(value === '' ? null : Number(value), type);
    }, [type]);

    const handleBlur = useCallback((value: string) => {
        if (resourceType === 'milestone' && value === '') {
            onChange(0, type);
        }
    }, [resourceType]);

    return (
        <FieldDefault
            small={true}
            type="number"
            value={offset?.toString()}
            placeholder={ t('Set') + '...' }
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
}

const ICONS = {
    task: <IconTask />,
    subtask: <IconSubtask />,
    milestone: <IconRoadmap />
}

interface ResourceFieldsProps {
    id: string,
    name: string,
    type: TemplateChildResource['type'],
    fields: TemplateField[],
    hasChildren?: boolean,
    collapsed?: boolean,
    onChange: (offset: TemplateField['offset'], type: TemplateField['type'], id: string) => void,
    onToggle?: () => void,

};

const ResourceFields = ({ id, type, name, fields, hasChildren, onChange, collapsed, onToggle }: ResourceFieldsProps) => {
    const { t } = useTranslation('common');
    const startField = fields.find(field => START_FIELDS.includes(field.type));
    const endField = fields.find(field => END_FIELDS.includes(field.type));

    const handleChange = useCallback((offset: TemplateField['offset'], type: TemplateField['type']) => {
        onChange(offset, type, id);
    }, [id]);

    return (
        <div className="fields-row">
            <div className="fields-cell cell-xxs resource-toggle" onClick={onToggle}>{ hasChildren ? (collapsed ? <IconArrowRight /> : <IconArrowDown />) : null }</div>

            <div className="fields-cell cell-xs">{ type ? ICONS[type] : t('N/A') }</div>

            <div className="fields-cell cell">
                <div className="resource-name">{ name ?  name : t('N/A') }</div>
            </div>

            <div className="fields-cell cell-s">
                {
                    startField ? <FieldItem key="start" resourceType={type} type={startField.type} offset={startField.offset} onChange={handleChange} /> : t('N/A')
                }
            </div>

            <div className="fields-cell cell-s">
                {
                    endField ? <FieldItem key="end" resourceType={type} type={endField.type} offset={endField.offset} onChange={handleChange} /> : t('N/A')
                }
            </div>
        </div>
    );
}

export default ResourceFields;
